import type { Note } from '../types/note';

interface NoteListProps {
  notes: Note[];
  onNoteClick: (note: Note) => void;
  onDeleteNote: (id: string) => void;
}

const categoryColors: Record<string, string> = {
  idea: 'bg-purple-100 text-purple-800',
  expense: 'bg-green-100 text-green-800',
  important: 'bg-red-100 text-red-800',
  private: 'bg-gray-100 text-gray-800',
  reminder: 'bg-yellow-100 text-yellow-800',
  bookmark: 'bg-blue-100 text-blue-800',
};

export default function NoteList({ notes, onNoteClick, onDeleteNote }: NoteListProps) {
  if (notes.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg">No notes yet</p>
        <p className="text-sm mt-2">Create your first note to get started</p>
      </div>
    );
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {notes.map((note) => (
        <div
          key={note.id}
          className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNoteClick(note)}
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate flex-1">
              {note.title}
            </h3>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteNote(note.id);
              }}
              className="text-gray-400 hover:text-red-600 ml-2"
              title="Delete note"
            >
              ×
            </button>
          </div>

          <p className="text-gray-600 text-sm line-clamp-3 mb-3">{note.content}</p>

          {/* Display image thumbnails if available */}
          {note.images && note.images.length > 0 && (
            <div className="flex gap-1 mb-3 overflow-x-auto">
              {note.images.slice(0, 3).map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-16 h-16 object-cover rounded border"
                />
              ))}
              {note.images.length > 3 && (
                <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-600">
                  +{note.images.length - 3}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between items-center">
            <span
              className={`px-2 py-1 rounded text-xs font-medium ${
                categoryColors[note.category] || 'bg-gray-100 text-gray-800'
              }`}
            >
              {note.category}
            </span>
            <span className="text-xs text-gray-500">{formatDate(note.updatedAt)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
